import { Box, Button, Container } from '@mui/material';
import type { RouteComponentProps } from '@reach/router';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';
import BloodTypeModalContent from '@components/Modal/Content/BloodTypeModalContent';
import TerraSyncButton from '@components/TerraSyncButton';
import VitalSignCard from '@components/VitalSignCard/VitalSignCard';

import { isProduction } from '@constants/env';

import { useGetBloodType } from '@hooks/api/lab-result';
import { useModal } from '@hooks/useModal';
import { useSession } from '@hooks/useSession';

import type { LabResultObservationRecord } from '@models/lab-result';
import type { ObservationRecord } from '@models/observation';
import { TrackerType, HEALTH_TRACKER_MAP } from '@models/vital-sign';

const messages = defineMessages({
  addTestReading: {
    id: 'healthTracker.addTestReading',
    defaultMessage: 'Add Test Reading',
  },
});

// import type { RecentVitalSign } from '@utils/vitalSigns';

const HealthTrackerMain: React.FC<
  RouteComponentProps & {
    title: string;
    records: Record<
      string,
      LabResultObservationRecord | ObservationRecord | null
    >;
    category: TrackerType;
    loading: boolean;
  }
> = ({ category, loading, records }) => {
  const { backRoute } = HEALTH_TRACKER_MAP[category];
  const { formatMessage } = useIntl();
  const { closeModal, openModal, setContent } = useModal();
  const {
    state: { isCurator, isPractitioner },
  } = useSession();
  const { data: bloodType } = useGetBloodType(category !== 'body');

  const showAddTestReading =
    !isProduction &&
    (isCurator || isPractitioner) &&
    (category === TrackerType.Body || category === TrackerType.Fitness);

  const handleOpenModal = (bloodType?: LabResultObservationRecord) => {
    openModal({
      width: 400,
      props: {
        height: '300px',
      },
      children: null,
    });
    setContent({
      children: (
        <BloodTypeModalContent onSubmit={closeModal} bloodType={bloodType} />
      ),
    });
  };

  return (
    <Container maxWidth="md">
      <Box
        my={{
          xs: 1,
          md: 3,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          {showAddTestReading && (
            <Button
              onClick={() => navigate(`${backRoute}add`)}
              startIcon={<Icon name="plus" type="fas" />}
              sx={{
                border: ({ palette }) => `1px solid ${palette.info.main}`,
                color: ({ palette }) => palette.info.main,
                textTransform: 'capitalize',
                fontSize: 14,
              }}
              variant="outlined"
            >
              {formatMessage(messages.addTestReading)}
            </Button>
          )}
          <TerraSyncButton />
        </Box>
        {/* Auto-fit rather than a hard md:6 split, so the middle widths get
        two columns instead of dropping to one full-width row. Capped and
        centred so a wide viewport does not leave the cards against the left
        edge with dead space beside them. */}
        <Box
          display="grid"
          gap={1}
          gridTemplateColumns="repeat(auto-fit, minmax(320px, 1fr))"
          maxWidth={880}
          mx="auto"
          pb={4}
        >
          {Object.entries(records).map(([code, observation]) => {
            const type = code.replace(/_/g, '-');
            return (
              <VitalSignCard
                code={type}
                key={`latest-${code}`}
                loading={loading}
                onClick={() => {
                  if (type === 'blood-type') {
                    if (isCurator) {
                      handleOpenModal(bloodType);
                    }
                  } else {
                    navigate(`${backRoute}${type}`);
                  }
                }}
                record={observation ?? undefined}
              />
            );
          })}
        </Box>
      </Box>
    </Container>
  );
};

export default HealthTrackerMain;
